import { FileText, Mail, ScanLine } from 'lucide-react'
import type {
  ParticipantCompliancePerson,
  ParticipantComplianceState,
} from '@/shared/domain'
import { Button } from '@/shared/ui/primitives'
import { cn } from '@/shared/ui/cn'

export type ParticipantDocumentsStripProps = {
  person: ParticipantCompliancePerson
  documentNames: Record<string, string>
  canAddEmail: boolean
  canMatch: boolean
  disabled?: boolean
  testId?: string
  onAddEmail: (person: ParticipantCompliancePerson) => void
  onMatch: (person: ParticipantCompliancePerson) => void
}

const STRIP: Record<ParticipantComplianceState, { label: string; className: string }> = {
  signed: { label: 'Signed', className: 'border-sage/20 bg-sage-soft text-sage' },
  missing: { label: 'Missing', className: 'border-amber/25 bg-amber-soft text-amber' },
  expired: {
    label: 'Expired',
    className: 'border-destructive/20 bg-destructive/10 text-destructive',
  },
  no_email: { label: 'No email', className: 'bg-muted text-muted-foreground' },
}

function missingDocumentsLabel(
  person: ParticipantCompliancePerson,
  documentNames: Record<string, string>,
): string {
  const names = person.missingTemplateIds
    .map((id) => documentNames[id])
    .filter((name): name is string => Boolean(name))
  return names.length > 0 ? names.join(', ') : 'Required documents'
}

function stripText(
  person: ParticipantCompliancePerson,
  documentNames: Record<string, string>,
): string {
  if (person.state === 'signed') return 'All required documents'
  const documents = missingDocumentsLabel(person, documentNames)
  return person.state === 'no_email' ? `Can’t be sent ${documents}` : documents
}

function canMatchQrSignature(person: ParticipantCompliancePerson): boolean {
  return (
    person.userId !== undefined && (person.state === 'missing' || person.state === 'expired')
  )
}

export function ParticipantDocumentsStrip({
  person,
  documentNames,
  canAddEmail,
  canMatch,
  disabled,
  testId,
  onAddEmail,
  onMatch,
}: ParticipantDocumentsStripProps) {
  const strip = STRIP[person.state]
  const showAddEmail = canAddEmail && person.state === 'no_email'
  const showMatch = canMatch && canMatchQrSignature(person)
  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-t px-3 py-2 text-sm',
        strip.className,
      )}
      data-testid={testId}
    >
      <div className="flex min-w-0 items-center gap-2">
        <FileText className="size-4 shrink-0" />
        <span className="font-semibold">{strip.label}</span>
        <span className="min-w-0 truncate text-foreground">
          {stripText(person, documentNames)}
        </span>
      </div>
      {showAddEmail ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="bg-card"
          onClick={() => onAddEmail(person)}
          disabled={disabled}
        >
          <Mail className="size-4" />
          Add email
        </Button>
      ) : null}
      {showMatch ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="bg-card"
          onClick={() => onMatch(person)}
          disabled={disabled}
        >
          <ScanLine className="size-4" />
          Match a QR signature
        </Button>
      ) : null}
    </div>
  )
}
